import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spinner, Form, Button, Badge, Modal } from 'react-bootstrap';
import {
  FolderOpen, RefreshCw, Download, ExternalLink, FileText, Receipt,
  X, FileSpreadsheet, File, Image as ImageIcon,
  Shield, Calendar, Upload, Tag as TagIcon, Edit2, Trash2, Archive,
  AlertTriangle, Save, HardDrive, Check, CheckSquare, User, Landmark,
  Sparkles,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import PageHeader from '../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../Common/ListToolbar';
import EmptyState from '../Common/EmptyState';
import { useUrlState } from '../../../hooks/useUrlState';
import { useListControls, useViewMode } from '../../../hooks/useListControls';
import * as api from '../../../services/api/documentsApi';
import { generateBrief } from '../../../services/InboxService';

// ─── CATEGORY META ─────────────────────────────────────────────────────

const CATEGORY_META = {
  expense:          { label: 'Kvitto (utlägg)',     bg: '#e0f2fe', color: '#0369a1', Icon: Receipt },
  supplier_invoice: { label: 'Leverantörsfaktura',  bg: '#fef3c7', color: '#92400e', Icon: FileText },
  customer_invoice: { label: 'Kundfaktura',         bg: '#dcfce7', color: '#166534', Icon: FileText },
  verification:     { label: 'Verifikationsbilaga', bg: '#ede9fe', color: '#5b21b6', Icon: FileText },
  vat_report:       { label: 'Momsrapport',         bg: '#ffedd5', color: '#9a3412', Icon: FileText },
  quote:            { label: 'Offert',              bg: '#cffafe', color: '#155e75', Icon: FileText },
  employee_document:{ label: 'Anställningsdokument',bg: '#fce7f3', color: '#9d174d', Icon: User },
  tax_document:     { label: 'Skatteunderlag',      bg: '#e0e7ff', color: '#3730a3', Icon: Landmark },
  archived:         { label: 'Arkiverat',           bg: '#f0fdf4', color: '#15803d', Icon: Archive },
  stale_upload:     { label: 'Väntande',            bg: '#fef2f2', color: '#b91c1c', Icon: AlertTriangle },
  orphaned:         { label: 'Saknar koppling',     bg: '#fef2f2', color: '#b91c1c', Icon: AlertTriangle },
  unlinked:         { label: 'Övrigt',              bg: '#4b5563', color: '#ffffff', Icon: File },
};

const isImage = (ct) => (ct || '').startsWith('image/');
const isPDF = (ct) => ct === 'application/pdf' || (ct || '').includes('pdf');
const isOffice = (ct) => /wordprocessing|spreadsheet|presentation|msword|excel|powerpoint/i.test(ct || '');

const IconFor = ({ contentType, size = 40 }) => {
  if (isImage(contentType)) return <ImageIcon size={size} />;
  if (isPDF(contentType)) return <FileText size={size} />;
  if (isOffice(contentType)) return <FileSpreadsheet size={size} />;
  return <File size={size} />;
};

const BLOB_UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;
const displayName = (doc) => doc.source?.title || doc.metadata?.description || (doc.blobName || '').replace(BLOB_UUID_PREFIX, '');
const fmtSize = (bytes) => { if (!bytes) return '—'; const kb = bytes / 1024; return kb < 1024 ? `${kb.toFixed(1)} kB` : `${(kb / 1024).toFixed(2)} MB`; };
const fmtDate = (d) => (d ? format(new Date(d), 'yyyy-MM-dd HH:mm') : '—');

// Hook: fetch a blob via authenticated API and return a blob:URL for use in
// <img src> / <iframe src>. Revokes on unmount to avoid memory leaks. Gated
// behind `enabled` so grid cards can hold off until they're visible.
function useBlobUrl(companyId, blobName, enabled = true) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!enabled || !blobName) return undefined;
    let cancelled = false;
    let objectUrl = null;
    api.fetchContentBlob(companyId, blobName)
      .then((u) => { if (cancelled) { URL.revokeObjectURL(u); } else { objectUrl = u; setUrl(u); } })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, blobName, enabled]);
  return { url, error };
}

// Defer content fetch until the element is scrolled near the viewport.
// Returns [ref, visible]. Graceful fallback for environments without IO.
function useLazyVisibility(rootMargin = '200px') {
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { setVisible(true); io.disconnect(); return; }
    }, { rootMargin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [rootMargin]);
  return [ref, visible];
}

// Image with authenticated fetch.
const BlobImage = ({ companyId, blobName, alt, className, style }) => {
  const [ref, visible] = useLazyVisibility();
  const { url, error } = useBlobUrl(companyId, blobName, visible);
  if (error) return <div ref={ref} className={className} style={style}><ImageIcon size={40} /></div>;
  return <img ref={ref} src={url || undefined} alt={alt} className={className} style={style} />;
};

// PDF thumbnail — pdfjs-dist renders page 1 to a canvas and converts to a
// data URL so we don't keep a full PDF parsed in memory per card. Cached
// in-module by blob_name so scrolling back up doesn't re-render.
// Much lighter than <object> embeds for grids with >20 PDFs.
let pdfjsLibPromise = null;
const pdfThumbCache = new Map(); // blob_name → data URL

const loadPdfjs = () => {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist').then((pdfjs) => {
      // Worker via CDN — we already bundle pdfjs-dist, so pin the same version.
      // Using the ESM worker build.
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
      return pdfjs;
    });
  }
  return pdfjsLibPromise;
};

const BlobPdfThumb = ({ companyId, blobName }) => {
  const [ref, visible] = useLazyVisibility();
  const [thumbUrl, setThumbUrl] = useState(() => pdfThumbCache.get(blobName) || null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!visible || thumbUrl || error) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        const buffer = await api.fetchContentArrayBuffer(companyId, blobName);
        const pdf = await pdfjs.getDocument({ data: buffer }).promise;
        const page = await pdf.getPage(1);
        // scale 0.75 renders A4 at ~446×631 — sharp enough for the "large"
        // (280px) tile while staying small enough to JPEG-compress cheaply.
        const viewport = page.getViewport({ scale: 0.75 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        if (!cancelled) {
          pdfThumbCache.set(blobName, dataUrl);
          setThumbUrl(dataUrl);
        }
        pdf.destroy();
      } catch (err) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, companyId, blobName, thumbUrl, error]);

  if (error) {
    return <div ref={ref} className="doc-card-thumb-fallback"><FileText size={48} /></div>;
  }
  if (!thumbUrl) {
    return (
      <div ref={ref} className="doc-card-thumb-fallback">
        {visible ? <Spinner animation="border" size="sm" /> : <FileText size={48} />}
      </div>
    );
  }
  return (
    <div ref={ref} className="doc-card-pdf-wrap">
      <img src={thumbUrl} alt="" loading="lazy" />
    </div>
  );
};

// PDF iframe used for full-size preview inside the detail panel.
const BlobIframe = ({ companyId, blobName, title }) => {
  const { url } = useBlobUrl(companyId, blobName);
  if (!url) return <div style={{ padding: 32, color: '#9ca3af' }}><Spinner animation="border" size="sm" /></div>;
  return <iframe src={url} title={title} />;
};

// This page had view mode and card size in localStorage before the shared
// hooks existed, under its own key names. Carry the stored preference over
// once so converting doesn't quietly reset everyone to the defaults; the old
// keys are then removed so this can be deleted in a release or two.
const LEGACY_VIEW_KEYS = [
  ['docsViewMode', 'll.view.documents'],
  ['docsCardSize', 'll.view.documents.density'],
];

(function migrateLegacyViewKeys() {
  try {
    for (const [from, to] of LEGACY_VIEW_KEYS) {
      const stored = window.localStorage.getItem(from);
      if (stored && !window.localStorage.getItem(to)) window.localStorage.setItem(to, stored);
      if (stored) window.localStorage.removeItem(from);
    }
  } catch {
    /* private mode — the defaults are fine */
  }
})();

// ─── MAIN PAGE ─────────────────────────────────────────────────────────

const DocumentsPage = () => {
  const { companyId } = useParams();
  const [docs, setDocs] = useState([]);
  const [tags, setTags] = useState([]);
  const [usage, setUsage] = useState(null); // { usage:{storageBytes,…}, limits:{maxStorageGb}, plan }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(true);

  // Category, tag and search go in the URL — they describe what you're
  // looking at, so the view survives a refresh and can be pasted to a
  // colleague. There's no sort control on this page, so `sort` is unused.
  const { search, setSearch, debouncedSearch, filter: categoryFilter, setFilter: setCategoryFilter, isFiltered } =
    useListControls({ defaultFilter: 'all', filterKey: 'kategori', searchKey: 'q' });
  // Second filter, so it can't come from useListControls. '' means "all tags".
  // Always a string, since it round-trips through the query string.
  const [tagFilter, setTagFilter] = useUrlState('tagg', '');

  // How you like to look at the page stays local — a shared link shouldn't
  // force someone into your layout.
  const [viewMode, changeView] = useViewMode('documents', 'grid');
  const [cardSize, changeCardSize] = useViewMode('documents.density', 'medium', ['small', 'medium', 'large']);

  const [selected, setSelected] = useState(null); // the doc in the right-hand preview panel
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);

  // Multi-select for bulk actions. Keyed on blob_name (stable across filters).
  // Cleared on filter change so users don't accidentally delete hidden docs.
  const [checkedBlobs, setCheckedBlobs] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Rendering pagination — only render `renderLimit` docs, grow as the user
  // scrolls past the sentinel. Full `filtered` list stays in state so
  // scrolling back up doesn't re-fetch anything.
  const [renderLimit, setRenderLimit] = useState(60);
  const loadMoreRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [docsRes, tagsRes, usageRes] = await Promise.all([
        api.listDocuments(companyId),
        api.listTags(companyId).catch(() => ({ data: { tags: [] } })),
        api.getResourceUsage(companyId).catch(() => null), // non-blocking
      ]);
      setConfigured(docsRes.data?.configured !== false);
      setDocs(docsRes.data?.documents || []);
      setTags(tagsRes.data?.tags || []);
      setUsage(usageRes?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda dokument.');
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // ─── Multi-select helpers ─────────────────────────────────────────
  const toggleChecked = useCallback((blobName) => {
    setCheckedBlobs((prev) => {
      const next = new Set(prev);
      if (next.has(blobName)) next.delete(blobName); else next.add(blobName);
      return next;
    });
  }, []);
  const clearChecked = useCallback(() => setCheckedBlobs(new Set()), []);

  const categoryCounts = useMemo(() => {
    const counts = { all: docs.length };
    for (const d of docs) counts[d.category] = (counts[d.category] || 0) + 1;
    return counts;
  }, [docs]);

  // Only categories actually present get a chip — the archive would otherwise
  // open on twelve chips, most of them zero.
  const categoryChips = useMemo(() => [
    { key: 'all', label: 'Alla', count: categoryCounts.all },
    ...Object.entries(CATEGORY_META)
      .filter(([key]) => categoryCounts[key])
      .map(([key, meta]) => ({
        key,
        label: meta.label,
        count: categoryCounts[key],
        color: { text: meta.color, fill: meta.bg },
      })),
  ], [categoryCounts]);

  const tagChips = useMemo(() => [
    { key: '', label: 'Alla' },
    ...tags.map((t) => ({ key: String(t.tagId), label: t.name, color: t.color })),
  ], [tags]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return docs.filter((d) => {
      if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
      // tagFilter came off the query string, so compare as strings — tagIds
      // arrive from the API as numbers.
      if (tagFilter && !d.tags?.some((t) => String(t.tagId) === tagFilter)) return false;
      if (q) {
        const haystack = [
          displayName(d), d.source?.title, d.source?.kind, d.contentType,
          d.metadata?.description, ...(d.tags?.map((t) => t.name) || []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [docs, debouncedSearch, categoryFilter, tagFilter]);

  // Reset render limit + clear multi-select whenever the visible set changes.
  // Not clearing selection on filter change would be a footgun ("delete 50"
  // while some are filtered-out and therefore invisible).
  useEffect(() => {
    setRenderLimit(60);
    setCheckedBlobs(new Set());
  }, [categoryFilter, tagFilter, debouncedSearch, viewMode]);

  // Infinite-scroll sentinel — bump renderLimit by 60 when the end is near.
  // Deps include renderLimit: IntersectionObserver only fires on threshold
  // *crossings*, so if bumping the limit doesn't scroll the sentinel out of
  // view, no further event fires. Re-observing each limit change retriggers
  // the callback (observer.observe emits the current state immediately), so
  // we keep loading until the sentinel leaves the rootMargin or hasMore=false.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) {
        setRenderLimit((n) => n + 60);
      }
    }, { rootMargin: '400px' });
    io.observe(node);
    return () => io.disconnect();
  }, [loading, renderLimit]);

  // Keyboard: Esc closes the preview modal. Arrow-key document-navigation
  // was intentionally dropped — users should only be looking at the doc
  // they clicked on.
  useEffect(() => {
    if (!selected) return undefined;
    const handler = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selected]);

  // Generate a brief from the card hover-action. Patches the parent
  // docs cache + selected so the modal and the card overlay see the
  // result without a list refetch. Mirrors the in-modal handler but
  // tracks a per-blobName "generating" state so the card can show a
  // spinner without making every card re-render.
  const [generatingBriefs, setGeneratingBriefs] = useState(() => new Set());
  const handleCardGenerateBrief = useCallback(async (doc) => {
    if (!doc?.blobName || !companyId) return;
    if (generatingBriefs.has(doc.blobName)) return;
    setGeneratingBriefs((prev) => { const next = new Set(prev); next.add(doc.blobName); return next; });
    try {
      const result = await generateBrief(companyId, doc.blobName, { entityType: 'document' });
      const newBrief = result?.brief || null;
      const patch = (d) => (d.blobName === doc.blobName
        ? { ...d, metadata: { ...(d.metadata || {}), ai_brief: newBrief } }
        : d);
      setDocs((prev) => prev.map(patch));
      setSelected((prev) => (prev && prev.blobName === doc.blobName ? patch(prev) : prev));
      toast.success('AI-sammanfattning genererad');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Kunde inte generera sammanfattning';
      toast.error(msg);
    } finally {
      setGeneratingBriefs((prev) => { const next = new Set(prev); next.delete(doc.blobName); return next; });
    }
  }, [companyId, generatingBriefs]);

  const handleDelete = async (doc) => {
    if (doc.canDelete === false) {
      await Swal.fire({
        title: 'Skyddat dokument',
        text: doc.protectionReason || 'Skyddat av Bokföringslagen.',
        icon: 'info',
      });
      return;
    }
    const confirm = await Swal.fire({
      title: `Ta bort ${displayName(doc)}?`,
      text: 'Filen raderas permanent från Azure och från arkivet.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ta bort permanent',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;
    try {
      await api.deleteDocument(companyId, doc.blobName);
      toast.success('Dokument borttaget.');
      setSelected(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.message || 'Kunde inte ta bort.'); }
  };

  const handleArchive = async (doc) => {
    try {
      await api.archiveDocument(companyId, doc.blobName);
      toast.success('Dokument arkiverat.');
      await load();
    } catch (err) { toast.error(err.response?.data?.message || 'Kunde inte arkivera.'); }
  };

  const handleBulkDelete = async () => {
    const checked = Array.from(checkedBlobs);
    if (checked.length === 0) return;

    // Filter out docs the backend will reject anyway (Bokföringslagen-linked)
    // so the UI can be upfront about what'll actually happen.
    const byBlobName = new Map(docs.map((d) => [d.blobName, d]));
    const deletable = checked.filter((n) => byBlobName.get(n)?.canDelete !== false);
    const protectedCount = checked.length - deletable.length;

    if (deletable.length === 0) {
      await Swal.fire({
        title: 'Skyddade dokument',
        html: `Alla ${protectedCount} valda dokument är skyddade av Bokföringslagen (kopplade till verifikat/faktura).<br>De kan inte raderas under bevarandetiden (7 år).`,
        icon: 'info',
      });
      return;
    }

    const confirm = await Swal.fire({
      title: `Ta bort ${deletable.length} dokument?`,
      html: protectedCount > 0
        ? `${deletable.length} dokument raderas permanent.<br><small>${protectedCount} skyddade av Bokföringslagen hoppas över.</small>`
        : 'Filerna raderas permanent från Azure och från arkivet.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Ta bort ${deletable.length} filer`,
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(deletable.map((n) => api.deleteDocument(companyId, n)));
    setBulkBusy(false);
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (fail === 0) toast.success(`${ok} dokument borttagna${protectedCount ? ` (${protectedCount} skyddade hoppades över)` : ''}.`);
    else if (ok === 0) toast.error(`Kunde inte ta bort — alla ${fail} misslyckades.`);
    else toast.warning(`${ok} borttagna, ${fail} misslyckades.`);
    clearChecked();
    setSelected(null);
    await load();
  };

  return (
    <>
      <style>{css}</style>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {!configured && (
        <Alert variant="warning">
          <strong>Azure Blob Storage är inte konfigurerat.</strong> Sätt
          {' '}<code>STORAGE_CONNECTION_STRING</code> i backend-miljön för att aktivera dokumentarkivet.
        </Alert>
      )}

      <PageHeader
        icon={FolderOpen}
        title="Dokumentarkiv"
        subtitle="Digitalt arkiv över alla bilagor — kvitton, fakturor, avtal, verifikationsunderlag."
        actions={
          <div className="docs-compliance" title="Bokföringslagen 7 kap 2§">
            <Shield size={14} />
            <span>7 års bevarande · digital lagring enligt BFNAR 2013:2</span>
          </div>
        }
      />

      {/* Storage quota widget */}
      <StorageQuota usage={usage} />

      {/* Bulk action bar — only when at least one doc is checked */}
      {checkedBlobs.size > 0 && (
        <div className="docs-bulk-bar">
          <div className="docs-bulk-info">
            <CheckSquare size={16} className="me-2" />
            <strong>{checkedBlobs.size}</strong>
            <span className="ms-1">markerade</span>
          </div>
          <div className="docs-bulk-actions">
            <Button variant="outline-secondary" size="sm" onClick={clearChecked}>
              Avmarkera
            </Button>
            <Button variant="danger" size="sm" onClick={handleBulkDelete} disabled={bulkBusy}>
              {bulkBusy ? <Spinner size="sm" animation="border" /> : <><Trash2 size={14} className="me-1" /> Ta bort {checkedBlobs.size}</>}
            </Button>
          </div>
        </div>
      )}

      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Sök filnamn, beskrivning, taggar…',
        }}
        // Card size controls the grid and nothing else, so it's absent in
        // list view rather than sitting there inert.
        density={viewMode === 'grid' ? { value: cardSize, onChange: changeCardSize } : undefined}
        view={{ value: viewMode, onChange: changeView, modes: ['grid', 'list'] }}
        actions={
          <>
            <Button variant="primary" size="sm" onClick={() => setShowUpload(true)}>
              <Upload size={14} className="me-1" /> Ladda upp
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} className="me-1" /> Uppdatera
            </Button>
          </>
        }
      />

      {/* Both chip rows sit under the toolbar, as they do on every other list
          page: search is the coarse control, the chips refine what it found. */}

      {/* Accent colouring: each category has a house colour, but twelve of
          them showing at once would be a rainbow, so the colour appears only
          on the selected one. */}
      <FilterChips
        className="docs-category-chips"
        chips={categoryChips}
        value={categoryFilter}
        onChange={setCategoryFilter}
      />

      {/* Identity colouring: tags are user-defined and the same colour marks
          them on every card, so they stay coloured throughout. */}
      {tags.length > 0 && (
        <div className="docs-tags-filter">
          <span className="docs-tags-label"><TagIcon size={12} /> Taggar:</span>
          <FilterChips
            chips={tagChips}
            value={tagFilter}
            // Clicking the selected tag clears it, as it did before — the
            // "Alla" chip isn't the only way back out.
            onChange={(key) => setTagFilter(key === tagFilter ? '' : key)}
          />
        </div>
      )}

      <div className="docs-main">
        <div className="docs-results">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : filtered.length === 0 ? (
            // An archive with nothing in it and an archive whose filter
            // matches nothing are different situations; the old copy
            // ("Inga dokument matchar filtret") was wrong for the first.
            // isFiltered doesn't know about the tag row, hence the || .
            isFiltered || tagFilter ? (
              <EmptyState variant="no-matches" query={debouncedSearch} />
            ) : (
              <EmptyState
                icon={FolderOpen}
                title="Inga dokument än"
                description="Allt som bifogas en faktura, ett utlägg eller ett verifikat hamnar här automatiskt. Du kan också ladda upp avtal, policyer och certifikat direkt."
                actions={
                  <Button variant="primary" size="sm" onClick={() => setShowUpload(true)}>
                    <Upload size={14} className="me-1" /> Ladda upp
                  </Button>
                }
              />
            )
          ) : (() => {
            // Rendering pagination — slice is cheap, IntersectionObserver
            // grows `renderLimit` when the sentinel hits the viewport.
            const rendered = filtered.slice(0, renderLimit);
            const hasMore = filtered.length > rendered.length;
            const cardProps = (d) => ({
              doc: d,
              selected: selected?.blobName === d.blobName,
              checked: checkedBlobs.has(d.blobName),
              onToggleChecked: () => toggleChecked(d.blobName),
              companyId,
              onClick: () => setSelected(d),
              onDelete: handleDelete,
              onEdit: setEditingDoc,
              onDownload: (doc) => api.downloadContentBlob(companyId, doc.blobName, displayName(doc)),
              onGenerateBrief: handleCardGenerateBrief,
              briefing: generatingBriefs.has(d.blobName),
            });
            return (
              <>
                {viewMode === 'grid' ? (
                  <div className={`docs-grid docs-grid-${cardSize}`}>
                    {rendered.map((d) => <DocCard key={d.blobName} {...cardProps(d)} />)}
                  </div>
                ) : (
                  <div className="docs-list">
                    {rendered.map((d) => <DocRow key={d.blobName} {...cardProps(d)} />)}
                  </div>
                )}
                {hasMore && (
                  <div ref={loadMoreRef} className="docs-load-more">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Laddar fler… ({rendered.length} av {filtered.length})</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>

      </div>

      <PreviewModal
        doc={selected}
        companyId={companyId}
        onClose={() => setSelected(null)}
        onEdit={() => { if (selected) { setEditingDoc(selected); setSelected(null); } }}
        onDelete={() => selected && handleDelete(selected)}
        onArchive={() => selected && handleArchive(selected)}
        onBriefGenerated={(blobName, newBrief) => {
          // Push the persisted brief into the parent docs cache so
          // closing/reopening the modal doesn't show the "Generate"
          // button again (and re-bill credits). Also update `selected`
          // so the modal's own useEffect doesn't reset on next render.
          const patch = (d) => (d.blobName === blobName
            ? { ...d, metadata: { ...(d.metadata || {}), ai_brief: newBrief } }
            : d);
          setDocs((prev) => prev.map(patch));
          setSelected((prev) => (prev && prev.blobName === blobName ? patch(prev) : prev));
        }}
      />

      <UploadModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        companyId={companyId}
        tags={tags}
        onUploaded={() => { setShowUpload(false); load(); }}
      />

      <EditMetadataModal
        doc={editingDoc}
        onClose={() => setEditingDoc(null)}
        companyId={companyId}
        tags={tags}
        onSaved={() => { setEditingDoc(null); load(); }}
      />
    </>
  );
};

// ─── LEAF COMPONENTS ───────────────────────────────────────────────────

// Storage quota meter. Hidden when the resource-usage endpoint fails or
// the plan lacks a storage cap — we don't want to render a bogus widget
// on tenants whose subscription isn't set up.
const StorageQuota = ({ usage }) => {
  if (!usage) return null;
  const usedBytes = Number(usage.usage?.storageBytes) || 0;
  const limitGb = Number(usage.limits?.maxStorageGb) || 0;
  if (!limitGb) return null;

  const limitBytes = limitGb * 1024 * 1024 * 1024;
  const pct = Math.min(100, (usedBytes / limitBytes) * 100);
  const planName = usage.plan?.name || usage.plan?.planName || usage.planName;

  const barColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#d97706' : 'var(--primary)';

  const fmtGb = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div className="docs-quota">
      <div className="docs-quota-icon"><HardDrive size={16} /></div>
      <div className="docs-quota-body">
        <div className="docs-quota-row">
          <span className="docs-quota-label">
            Lagring — <strong>{fmtGb(usedBytes)}</strong> av <strong>{limitGb} GB</strong>
            <span className="docs-quota-pct">({pct.toFixed(1)}%)</span>
          </span>
          {planName && <span className="docs-quota-plan">{planName}</span>}
        </div>
        <div className="docs-quota-track">
          <div className="docs-quota-fill" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        {pct >= 90 && (
          <div className="docs-quota-warning">
            Lagringsutrymmet är nästan fullt — nya uppladdningar kan blockeras. Uppgradera planen eller rensa bort gamla dokument.
          </div>
        )}
      </div>
    </div>
  );
};

const ExpiryBadge = ({ validTo }) => {
  if (!validTo) return null;
  const days = differenceInDays(new Date(validTo), new Date());
  if (days < 0) {
    return <Badge bg="danger" style={{ fontSize: 10 }}>Utgått</Badge>;
  }
  if (days <= 30) {
    return <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>Går ut om {days} dagar</Badge>;
  }
  return null;
};

const DocCard = ({ doc, selected, checked, onToggleChecked, onClick, onDelete, onEdit, onDownload, onGenerateBrief, briefing, companyId }) => {
  const meta = CATEGORY_META[doc.category] || CATEGORY_META.unlinked;
  const MetaIcon = meta.Icon;
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(doc); };
  const brief = doc.metadata?.ai_brief || null;
  const briefSummary = brief?.summary_sv?.trim() || null;
  return (
    <div
      className={`doc-card ${selected ? 'selected' : ''} ${checked ? 'checked' : ''}`}
      onClick={onClick}
      tabIndex={0} role="button"
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div className="doc-card-thumb">
        {isImage(doc.contentType) ? (
          <BlobImage companyId={companyId} blobName={doc.blobName} alt={displayName(doc)} />
        ) : isPDF(doc.contentType) ? (
          <BlobPdfThumb companyId={companyId} blobName={doc.blobName} />
        ) : (
          <IconFor contentType={doc.contentType} size={56} />
        )}
        {/* Selection checkbox — always visible when checked, hover-revealed otherwise */}
        <button
          type="button"
          className={`doc-card-checkbox ${checked ? 'checked' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleChecked(); }}
          aria-label={checked ? 'Avmarkera' : 'Markera'}
          aria-pressed={checked}
        >
          {checked ? <Check size={14} /> : null}
        </button>
        {/* Hover-reveal actions */}
        <div className="doc-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`doc-card-action ${brief ? 'doc-card-action-has-brief' : ''}`}
            onClick={stop(onGenerateBrief)}
            disabled={briefing}
            title={brief ? 'Generera om AI-sammanfattning' : 'Generera AI-sammanfattning (2 krediter)'}
          >
            {briefing ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} /> : <Sparkles size={14} />}
          </button>
          <button type="button" className="doc-card-action" onClick={stop(onDownload)} title="Ladda ner">
            <Download size={14} />
          </button>
          <button type="button" className="doc-card-action" onClick={stop(onEdit)} title="Redigera">
            <Edit2 size={14} />
          </button>
          <button
            type="button"
            className={`doc-card-action ${doc.canDelete === false ? '' : 'doc-card-action-danger'}`}
            onClick={stop(onDelete)}
            title={doc.canDelete === false ? (doc.protectionReason || 'Skyddat av Bokföringslagen') : 'Ta bort'}
            disabled={doc.canDelete === false}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="doc-card-body">
        {/* When a brief exists, it becomes the primary text — much more
            useful than the UUID-prefixed blob filename. The filename
            stays accessible via the tooltip on the line and in the
            modal. Otherwise we show the filename as before. */}
        {briefSummary ? (
          <div className="doc-card-brief" title={`${displayName(doc)}\n\n${briefSummary}`}>
            <Sparkles size={11} className="doc-card-brief-icon" />
            <span className="doc-card-brief-text">{briefSummary}</span>
          </div>
        ) : (
          <div className="doc-card-name" title={displayName(doc)}>{displayName(doc)}</div>
        )}
        <div className="doc-card-meta">
          <Badge style={{ background: meta.bg, color: meta.color, fontWeight: 500 }}>
            <MetaIcon size={10} className="me-1" style={{ verticalAlign: 'middle' }} />
            {meta.label}
          </Badge>
          <span className="doc-card-size">{fmtSize(doc.size)}</span>
        </div>
        {doc.metadata?.valid_to && (
          <div style={{ marginTop: 4 }}><ExpiryBadge validTo={doc.metadata.valid_to} /></div>
        )}
        {(doc.tags?.length > 0 || brief?.suggested_tags?.length > 0) && (
          <div className="doc-card-tags">
            {doc.tags?.slice(0, 3).map((t) => (
              <span key={t.tagId} className="doc-tag-chip" style={{ background: t.color + '20', color: t.color }}>{t.name}</span>
            ))}
            {/* AI-suggested tags appear with a Sparkles prefix so users
                can distinguish them from human-applied tags. */}
            {brief?.suggested_tags?.slice(0, 2).map((t) => (
              <span key={`ai-${t}`} className="doc-tag-chip doc-tag-chip-ai" title="AI-föreslagen tagg">
                <Sparkles size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{t}
              </span>
            ))}
            {doc.tags?.length > 3 && <span className="doc-tag-chip">+{doc.tags.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const DocRow = ({ doc, selected, checked, onToggleChecked, onClick, onDelete, onEdit, onDownload }) => {
  const meta = CATEGORY_META[doc.category] || CATEGORY_META.unlinked;
  const MetaIcon = meta.Icon;
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(doc); };
  return (
    <div className={`doc-row ${selected ? 'selected' : ''} ${checked ? 'checked' : ''}`} onClick={onClick} tabIndex={0} role="button">
      <button
        type="button"
        className={`doc-row-checkbox ${checked ? 'checked' : ''}`}
        onClick={(e) => { e.stopPropagation(); onToggleChecked(); }}
        aria-label={checked ? 'Avmarkera' : 'Markera'}
        aria-pressed={checked}
      >
        {checked ? <Check size={14} /> : null}
      </button>
      <div className="doc-row-icon"><IconFor contentType={doc.contentType} size={24} /></div>
      <div className="doc-row-name">
        {displayName(doc)}
        {doc.tags?.length > 0 && (
          <span className="doc-row-tags">
            {doc.tags.slice(0, 3).map((t) => (
              <span key={t.tagId} className="doc-tag-chip" style={{ background: t.color + '20', color: t.color }}>{t.name}</span>
            ))}
          </span>
        )}
      </div>
      <div className="doc-row-source">
        <Badge style={{ background: meta.bg, color: meta.color, fontWeight: 500 }}>
          <MetaIcon size={10} className="me-1" style={{ verticalAlign: 'middle' }} />{meta.label}
        </Badge>
      </div>
      <div className="doc-row-size">{fmtSize(doc.size)}</div>
      <div className="doc-row-date">{fmtDate(doc.lastModified)}</div>
      <div className="doc-row-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="doc-card-action" onClick={stop(onDownload)} title="Ladda ner"><Download size={14} /></button>
        <button type="button" className="doc-card-action" onClick={stop(onEdit)} title="Redigera"><Edit2 size={14} /></button>
        <button
          type="button"
          className={`doc-card-action ${doc.canDelete === false ? '' : 'doc-card-action-danger'}`}
          onClick={stop(onDelete)}
          title={doc.canDelete === false ? (doc.protectionReason || 'Skyddat av Bokföringslagen') : 'Ta bort'}
          disabled={doc.canDelete === false}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const PreviewModal = ({ doc, companyId, onClose, onEdit, onDelete, onArchive, onBriefGenerated }) => {
  // useBlobUrl must be called unconditionally — use a placeholder blobName
  // when the modal is closed so the hook contract holds.
  const blobName = doc?.blobName || '';
  const wantImage = !!doc && isImage(doc.contentType);
  const { url: previewImageUrl } = useBlobUrl(companyId, blobName, wantImage);

  // AI brief state. Seeded from doc.metadata.ai_brief on open; can be
  // refreshed by clicking "Generera sammanfattning". Lives in modal-local
  // state so the user sees the result immediately after generation
  // without needing a docs-list refetch.
  const [brief, setBrief] = useState(doc?.metadata?.ai_brief || null);
  const [briefing, setBriefing] = useState(false);

  // Reset brief state when the modal opens for a different doc.
  useEffect(() => {
    setBrief(doc?.metadata?.ai_brief || null);
  }, [doc?.blobName, doc?.metadata?.ai_brief]);

  const handleGenerateBrief = useCallback(async () => {
    if (!doc?.blobName || !companyId) return;
    setBriefing(true);
    try {
      const result = await generateBrief(companyId, doc.blobName, { entityType: 'document' });
      const newBrief = result?.brief || null;
      setBrief(newBrief);
      // Propagate to the parent so the persisted brief survives a
      // modal close/reopen without a list refetch — otherwise the
      // useEffect below re-seeds from the stale doc.metadata.
      if (newBrief && typeof onBriefGenerated === 'function') {
        onBriefGenerated(doc.blobName, newBrief);
      }
      toast.success('AI-sammanfattning genererad');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Kunde inte generera sammanfattning';
      toast.error(msg);
    } finally {
      setBriefing(false);
    }
  }, [doc?.blobName, companyId, onBriefGenerated]);

  if (!doc) return null;
  const meta = CATEGORY_META[doc.category] || CATEGORY_META.unlinked;
  const MetaIcon = meta.Icon;
  const isStale = doc.category === 'stale_upload';
  return (
    <Modal show={!!doc} onHide={onClose} size="xl" centered dialogClassName="docs-preview-modal">
      {/* Branded header — matches Upload/Edit modals */}
      <div className="pv-header">
        <button className="pv-close" onClick={onClose} aria-label="Stäng (Esc)">
          <X size={16} />
        </button>
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
          <div className="pv-header-icon" style={{ background: meta.bg, color: meta.color }}>
            <MetaIcon size={22} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="pv-title" title={displayName(doc)}>{displayName(doc)}</div>
            <div className="pv-subtitle">
              <Badge style={{ background: meta.bg, color: meta.color, fontWeight: 500, fontSize: 10 }}>{meta.label}</Badge>
              <span>·</span>
              <span>{fmtSize(doc.size)}</span>
              {doc.contentType && <><span>·</span><span>{doc.contentType}</span></>}
              {doc.metadata?.valid_to && (<><span>·</span><ExpiryBadge validTo={doc.metadata.valid_to} /></>)}
            </div>
          </div>
        </div>
      </div>

      <Modal.Body style={{ padding: 0 }}>
        <div className="pv-stage">
          {isImage(doc.contentType) ? (
            previewImageUrl
              ? <img src={previewImageUrl} alt={displayName(doc)} />
              : <div className="pv-loading"><Spinner animation="border" /></div>
          ) : isPDF(doc.contentType) ? (
            <BlobIframe companyId={companyId} blobName={doc.blobName} title={displayName(doc)} />
          ) : (
            <div className="pv-fallback">
              <div className="pv-fallback-icon"><IconFor contentType={doc.contentType} size={64} /></div>
              <p className="pv-fallback-text">Ingen förhandsvisning tillgänglig för den här filtypen.</p>
              <Button
                variant="primary" size="sm" className="mt-2"
                onClick={() => api.downloadContentBlob(companyId, doc.blobName, displayName(doc))}
              >
                <Download size={14} className="me-1" /> Ladda ner för att öppna
              </Button>
            </div>
          )}
        </div>

        <div className="pv-meta">
          {doc.metadata?.description && (
            <div className="pv-meta-row">
              <span className="pv-meta-label">Beskrivning</span>
              <span className="pv-meta-value">{doc.metadata.description}</span>
            </div>
          )}
          {doc.source?.title && doc.source.title !== displayName(doc) && (
            <div className="pv-meta-row">
              <span className="pv-meta-label">Kopplad till</span>
              <span className="pv-meta-value">{doc.source.title}</span>
            </div>
          )}
          <div className="pv-meta-row">
            <span className="pv-meta-label">Senast ändrad</span>
            <span className="pv-meta-value">{fmtDate(doc.lastModified)}</span>
          </div>
          {(doc.metadata?.valid_from || doc.metadata?.valid_to) && (
            <div className="pv-meta-row">
              <span className="pv-meta-label">Giltighet</span>
              <span className="pv-meta-value">
                {doc.metadata.valid_from || '—'} &rarr; {doc.metadata.valid_to || '—'}
              </span>
            </div>
          )}
          {doc.tags?.length > 0 && (
            <div className="pv-meta-row">
              <span className="pv-meta-label">Taggar</span>
              <span className="pv-meta-value">
                {doc.tags.map((t) => (
                  <span key={t.tagId} className="doc-tag-chip" style={{ background: t.color + '20', color: t.color }}>{t.name}</span>
                ))}
              </span>
            </div>
          )}
          {isStale && (
            <div className="pv-alert pv-alert-warn">
              <AlertTriangle size={14} /> Väntande uppladdning — behöver åtgärd
            </div>
          )}
          {doc.canDelete === false && (
            <div className="pv-alert pv-alert-info">
              <Shield size={14} /> {doc.protectionReason}
            </div>
          )}
        </div>

        {/* AI-sammanfattning. Either renders an existing brief or offers a
            "Generera sammanfattning" button. Costs ai_brief credits per
            generation; brief is persisted on document_metadata.ai_brief
            so re-opens the modal show the cached value without re-spending
            credits. "Generera om" lets the user explicitly re-spend. */}
        <div className="pv-meta" style={{ borderTop: '1px solid #e5e7eb' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <span className="pv-meta-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={14} /> AI-sammanfattning
            </span>
            {brief && (
              <Button
                variant="link"
                size="sm"
                onClick={handleGenerateBrief}
                disabled={briefing}
                style={{ padding: 0, fontSize: 12 }}
              >
                {briefing ? 'Genererar…' : 'Generera om'}
              </Button>
            )}
          </div>
          {brief ? (
            <DocumentBrief brief={brief} />
          ) : (
            <div style={{
              background: '#f9fafb',
              border: '1px dashed #d1d5db',
              borderRadius: 8,
              padding: 12,
              textAlign: 'center',
              fontSize: 13,
              color: '#6b7280',
            }}>
              <p style={{ margin: '0 0 8px' }}>
                Få en AI-sammanfattning med dokumenttyp, nyckelparter och föreslagna taggar.
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleGenerateBrief}
                disabled={briefing}
              >
                {briefing ? <Spinner animation="border" size="sm" /> : <Sparkles size={14} />}
                <span className="ms-1">Generera sammanfattning</span>
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>

      <div className="pv-footer">
        <div className="pv-footer-hint">Esc stänger</div>
        <div className="pv-footer-actions">
          {isStale && (
            <Button variant="outline-success" size="sm" onClick={onArchive} title="Behåll som arkiv-dokument">
              <Archive size={14} className="me-1" /> Behåll
            </Button>
          )}
          <button
            className="pv-icon-btn"
            onClick={onEdit}
            aria-label="Redigera metadata"
            title="Redigera metadata"
          >
            <Edit2 size={16} />
          </button>
          <button
            className="pv-icon-btn pv-icon-btn-danger"
            onClick={onDelete}
            disabled={doc.canDelete === false}
            aria-label="Ta bort"
            title={doc.canDelete === false ? (doc.protectionReason || 'Skyddat av Bokföringslagen') : 'Ta bort'}
          >
            <Trash2 size={16} />
          </button>
          <button
            className="pv-icon-btn"
            onClick={async () => {
              const u = await api.fetchContentBlob(companyId, doc.blobName);
              window.open(u, '_blank', 'noopener,noreferrer');
            }}
            aria-label="Öppna i ny flik"
            title="Öppna i ny flik"
          >
            <ExternalLink size={16} />
          </button>
          <Button
            variant="primary"
            onClick={() => api.downloadContentBlob(companyId, doc.blobName, displayName(doc))}
          >
            <Download size={16} className="me-1" /> Ladda ner
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── DOCUMENT BRIEF ────────────────────────────────────────────────────
// Renderer for the structured AI brief shape stored on
// document_metadata.ai_brief. Different schema from the inbox-item
// brief (no suggested BAS account; instead key entities/dates/tags
// because documents are broader than just invoices).
//   { summary_sv, document_type, key_entities[], key_dates[], suggested_tags[] }
const DocumentBrief = ({ brief }) => {
  if (!brief || typeof brief !== 'object') return null;
  return (
    <div style={{
      background: '#fefce8',
      border: '1px solid #fde68a',
      borderRadius: 8,
      padding: 12,
      fontSize: 13,
    }}>
      {brief.summary_sv && <p style={{ marginBottom: 8 }}>{brief.summary_sv}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 12 }}>
        {brief.document_type && (
          <Badge bg="light" text="dark" style={{ border: '1px solid #e5e7eb' }}>
            Typ: <strong>{brief.document_type}</strong>
          </Badge>
        )}
        {Array.isArray(brief.suggested_tags) && brief.suggested_tags.map((tag, i) => (
          <Badge key={i} bg="info" text="dark">{tag}</Badge>
        ))}
      </div>
      {Array.isArray(brief.key_entities) && brief.key_entities.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>Parter: </span>
          {brief.key_entities.join(' · ')}
        </div>
      )}
      {Array.isArray(brief.key_dates) && brief.key_dates.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <span style={{ color: '#6b7280', fontWeight: 500 }}>Datum: </span>
          {brief.key_dates.join(' · ')}
        </div>
      )}
    </div>
  );
};

// ─── UPLOAD MODAL ──────────────────────────────────────────────────────
// Styled to match UpdateTaxProfileModal / UpdateVacationBalanceModal —
// gradient header, info banner, icon-prefixed inputs, footer hint.

const UploadModal = ({ show, onClose, companyId, tags, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  const reset = () => {
    setFile(null); setDescription(''); setValidFrom(''); setValidTo('');
    setSelectedTags([]); setUploading(false); setDragOver(false);
  };
  const close = () => { reset(); onClose(); };
  const toggleTag = (tagId) => setSelectedTags((prev) =>
    prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) setFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadStandalone(companyId, file, {
        description: description.trim() || undefined,
        validFrom: validFrom || undefined,
        validTo: validTo || undefined,
        tagIds: selectedTags.length ? selectedTags : undefined,
      });
      toast.success('Dokument uppladdat.');
      reset();
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Uppladdning misslyckades.');
    } finally { setUploading(false); }
  };

  const fmtBytes = (b) => (b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} kB` : `${(b / (1024 * 1024)).toFixed(2)} MB`);

  return (
    <>
      <style>{uploadModalCss}</style>
      <Modal show={show} onHide={close} size="lg" backdrop="static" centered>
        <div className="up-modal-content">
          <div className="up-modal-header">
            <button className="up-close" onClick={close} aria-label="Stäng modal" disabled={uploading}>
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <Upload size={24} color="var(--primary)" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: 600 }}>Ladda upp dokument</h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Standalone-uppladdning till dokumentarkivet (avtal, policyer, certifikat, osv.)
                </p>
              </div>
            </div>
          </div>

          <div className="up-modal-body">
            <div className="up-info">
              <Shield size={16} className="up-info-icon" />
              <div className="up-info-text">
                <strong>Bokföringslagen 7 kap 2§:</strong> bokföringsmaterial bevaras i 7 år.
                Digital lagring är tillåten enligt BFNAR 2013:2. Använd utläggsflödet eller
                leverantörsfakturaflödet om dokumentet ska kopplas till en specifik post.
              </div>
            </div>

            {/* Drop zone / file picker */}
            <div
              className={`up-dropzone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input
                ref={fileInputRef} type="file"
                onChange={(e) => setFile(e.target.files[0] || null)}
                style={{ display: 'none' }}
              />
              {file ? (
                <>
                  <div className="up-file-icon"><FileText size={28} /></div>
                  <div className="up-file-body">
                    <div className="up-file-name">{file.name}</div>
                    <div className="up-file-meta">{fmtBytes(file.size)} · {file.type || 'okänd typ'}</div>
                  </div>
                  <button
                    type="button"
                    className="up-file-remove"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <>
                  <div className="up-dropzone-icon"><Upload size={28} /></div>
                  <div className="up-dropzone-text">
                    <strong>Dra hit en fil</strong> eller <span className="up-link">klicka för att välja</span>
                  </div>
                  <div className="up-dropzone-hint">PDF, bilder, Office-dokument, XML …</div>
                </>
              )}
            </div>

            {/* Metadata section */}
            <div className="up-section">
              <div className="up-section-title"><FileText size={16} /> Metadata</div>
              <div className="up-input-group mb-3">
                <label className="up-input-label">Beskrivning</label>
                <p className="up-input-desc">Hjälper sökbarheten i arkivet. T.ex. "Hyresavtal med XYZ AB, tecknat 2025-03-01".</p>
                <div className="up-input-container">
                  <div className="up-input-icon"><FileText size={16} /></div>
                  <textarea
                    className="up-input"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Kort beskrivning av dokumentet"
                  />
                </div>
              </div>
              <div className="up-grid-2">
                <div className="up-input-group">
                  <label className="up-input-label">Giltig från</label>
                  <p className="up-input-desc">För avtal/certifikat — lämna tomt annars.</p>
                  <div className="up-input-container">
                    <div className="up-input-icon"><Calendar size={16} /></div>
                    <input type="date" className="up-input" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                  </div>
                </div>
                <div className="up-input-group">
                  <label className="up-input-label">Giltig till</label>
                  <p className="up-input-desc">Aktiverar förfallovarningar i arkivet.</p>
                  <div className="up-input-container">
                    <div className="up-input-icon"><Calendar size={16} /></div>
                    <input type="date" className="up-input" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="up-section">
              <div className="up-section-title"><TagIcon size={16} /> Taggar</div>
              <p className="up-input-desc mb-2">Valfritt — för filtrering i arkivet.</p>
              <div className="d-flex flex-wrap gap-1">
                {tags.length === 0 ? (
                  <span className="text-muted" style={{ fontSize: 13 }}>Inga taggar definierade.</span>
                ) : tags.map((t) => (
                  <button
                    key={t.tagId}
                    type="button"
                    className={`docs-tag ${selectedTags.includes(t.tagId) ? 'active' : ''}`}
                    onClick={() => toggleTag(t.tagId)}
                    style={selectedTags.includes(t.tagId)
                      ? { background: t.color, color: 'white', borderColor: t.color }
                      : { borderColor: t.color, color: t.color }}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="up-modal-actions">
            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              * Fil krävs. Övriga fält är valfria.
            </div>
            <div className="d-flex gap-2">
              <button className="up-btn up-btn-cancel" onClick={close} disabled={uploading}>
                Avbryt
              </button>
              <button className="up-btn up-btn-save" onClick={handleUpload} disabled={!file || uploading}>
                {uploading ? (
                  <><div className="up-spinner" /> Laddar upp…</>
                ) : (
                  <><Upload size={16} /> Ladda upp</>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const uploadModalCss = `
  .up-modal-content { border-radius: 12px; overflow: hidden; border: none; }
  .up-modal-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 1.5rem 2rem; border-bottom: 1px solid #e2e8f0; position: relative;
  }
  .up-modal-body { padding: 1.5rem 2rem; background: #fff; max-height: 70vh; overflow-y: auto; }
  .up-close {
    position: absolute; top: 1rem; right: 1.5rem;
    background: #f1f5f9; border: 1px solid #e2e8f0; color: #64748b;
    width: 32px; height: 32px; border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.2s ease; cursor: pointer;
  }
  .up-close:hover { background: #e2e8f0; color: #475569; }

  .up-info {
    display: flex; gap: 10px; align-items: flex-start;
    background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
    padding: 12px 14px; margin-bottom: 1rem;
  }
  .up-info-icon { color: #0284c7; flex-shrink: 0; margin-top: 2px; }
  .up-info-text { font-size: 0.8125rem; color: #0c4a6e; line-height: 1.5; }

  /* Read-only "what you're editing" card at the top of EditMetadataModal. */
  .up-current {
    display: flex; gap: 12px; align-items: flex-start;
    background: var(--rgba-primary-1); border: 1px solid var(--primary);
    border-radius: 8px; padding: 12px 14px; margin-bottom: 1rem;
  }
  .up-current-icon { color: var(--primary); flex-shrink: 0; margin-top: 2px; }
  .up-current-body { flex: 1; min-width: 0; }
  .up-current-label {
    font-size: 0.6875rem; font-weight: 500; color: var(--primary);
    text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px;
  }
  .up-current-value { font-size: 0.875rem; color: #0f172a; display: flex; align-items: center; flex-wrap: wrap; }

  .up-dropzone {
    display: flex; align-items: center; gap: 14px;
    padding: 18px 20px; border: 2px dashed #cbd5e1; border-radius: 10px;
    background: #f8fafc; cursor: pointer; transition: all 0.15s ease;
    margin-bottom: 1.25rem;
  }
  .up-dropzone:hover, .up-dropzone.drag-over { border-color: var(--primary); background: var(--rgba-primary-1); }
  .up-dropzone.has-file { flex-direction: row; border-style: solid; border-color: var(--primary); background: var(--rgba-primary-1); }
  .up-dropzone-icon, .up-file-icon {
    width: 48px; height: 48px; border-radius: 10px;
    background: var(--rgba-primary-1); color: var(--primary);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .up-dropzone-text { font-size: 0.95rem; color: #334155; }
  .up-dropzone-hint { font-size: 0.75rem; color: #94a3b8; margin-left: 2px; }
  .up-link { color: var(--primary); font-weight: 500; text-decoration: underline; }
  .up-file-body { flex: 1; min-width: 0; }
  .up-file-name { font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .up-file-meta { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
  .up-file-remove {
    width: 30px; height: 30px; border-radius: 6px; border: none;
    background: rgba(255,255,255,0.8); color: #64748b; cursor: pointer;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .up-file-remove:hover { background: #fef2f2; color: #dc2626; }

  .up-section { margin-bottom: 1.25rem; }
  .up-section-title {
    font-size: 0.875rem; font-weight: 600; color: #1e293b;
    margin-bottom: 0.75rem;
    display: flex; align-items: center; gap: 0.5rem;
    padding-bottom: 0.5rem; border-bottom: 1px solid #f1f5f9;
  }
  .up-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  @media (max-width: 640px) { .up-grid-2 { grid-template-columns: 1fr; } }

  .up-input-group { display: flex; flex-direction: column; gap: 0.25rem; }
  .up-input-label { font-size: 0.875rem; font-weight: 500; color: #374151; margin: 0; }
  .up-input-desc { font-size: 0.75rem; color: #6b7280; margin: 0 0 0.4rem; line-height: 1.3; }
  .up-input-container { position: relative; display: flex; align-items: center; }
  .up-input-icon { position: absolute; left: 0.75rem; top: 0.9rem; z-index: 1; color: #6b7280; pointer-events: none; }
  .up-input {
    width: 100%; padding: 0.7rem 0.75rem 0.7rem 2.5rem;
    border: 1px solid #d1d5db; border-radius: 8px;
    font-size: 0.875rem; background: white; color: #374151;
    transition: all 0.2s ease; resize: vertical;
  }
  .up-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); }

  .up-modal-actions {
    background: #f8fafc; padding: 1.1rem 2rem; border-top: 1px solid #e2e8f0;
    display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  }
  .up-btn {
    border: none; border-radius: 8px; padding: 0.65rem 1.25rem;
    font-weight: 600; font-size: 0.875rem;
    display: inline-flex; align-items: center; gap: 0.5rem;
    cursor: pointer; transition: all 0.15s ease;
  }
  .up-btn-cancel { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
  .up-btn-cancel:hover { background: #e2e8f0; color: #475569; }
  .up-btn-save { background: var(--primary); color: white; }
  .up-btn-save:hover:not(:disabled) { filter: brightness(0.95); }
  .up-btn-save:disabled { background: #94a3b8; cursor: not-allowed; }
  .up-spinner {
    width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white; border-radius: 50%;
    animation: up-spin 0.8s linear infinite;
  }
  @keyframes up-spin { to { transform: rotate(360deg); } }
`;

// ─── EDIT METADATA MODAL ───────────────────────────────────────────────
// Same visual language as UploadModal — shares the up-* CSS block so both
// modals feel like siblings.

const EditMetadataModal = ({ doc, onClose, companyId, tags, onSaved }) => {
  const [description, setDescription] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setDescription(doc.metadata?.description || '');
    setValidFrom(doc.metadata?.valid_from || '');
    setValidTo(doc.metadata?.valid_to || '');
    setSelectedTags(doc.tags?.map((t) => t.tagId) || []);
  }, [doc]);

  if (!doc) return null;

  const toggleTag = (tagId) => setSelectedTags((prev) =>
    prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patchDocument(companyId, doc.blobName, {
        description: description.trim(),
        validFrom: validFrom || null,
        validTo: validTo || null,
        tagIds: selectedTags,
      });
      toast.success('Sparat.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kunde inte spara.');
    } finally { setSaving(false); }
  };

  const meta = CATEGORY_META[doc.category] || CATEGORY_META.unlinked;

  return (
    <>
      <style>{uploadModalCss}</style>
      <Modal show={!!doc} onHide={onClose} size="lg" backdrop="static" centered>
        <div className="up-modal-content">
          <div className="up-modal-header">
            <button className="up-close" onClick={onClose} aria-label="Stäng modal" disabled={saving}>
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <Edit2 size={24} color="var(--primary)" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: 600 }}>Redigera metadata</h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 480 }}>
                  {displayName(doc)}
                </p>
              </div>
            </div>
          </div>

          <div className="up-modal-body">
            {/* Read-only context of what's being edited */}
            <div className="up-current">
              <meta.Icon size={20} className="up-current-icon" />
              <div className="up-current-body">
                <div className="up-current-label">Dokumenttyp</div>
                <div className="up-current-value">
                  <Badge style={{ background: meta.bg, color: meta.color, fontWeight: 500 }}>{meta.label}</Badge>
                  <span className="ms-2 text-muted" style={{ fontSize: 12 }}>
                    {doc.contentType || '—'} · {(() => {
                      const b = doc.size || 0;
                      return b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} kB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div className="up-section">
              <div className="up-section-title"><FileText size={16} /> Metadata</div>
              <div className="up-input-group mb-3">
                <label className="up-input-label">Beskrivning</label>
                <p className="up-input-desc">Hjälper sökbarheten i arkivet.</p>
                <div className="up-input-container">
                  <div className="up-input-icon"><FileText size={16} /></div>
                  <textarea
                    className="up-input" rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Kort beskrivning av dokumentet"
                  />
                </div>
              </div>
              <div className="up-grid-2">
                <div className="up-input-group">
                  <label className="up-input-label">Giltig från</label>
                  <p className="up-input-desc">För avtal/certifikat.</p>
                  <div className="up-input-container">
                    <div className="up-input-icon"><Calendar size={16} /></div>
                    <input type="date" className="up-input" value={validFrom || ''} onChange={(e) => setValidFrom(e.target.value)} />
                  </div>
                </div>
                <div className="up-input-group">
                  <label className="up-input-label">Giltig till</label>
                  <p className="up-input-desc">Aktiverar förfallovarningar.</p>
                  <div className="up-input-container">
                    <div className="up-input-icon"><Calendar size={16} /></div>
                    <input type="date" className="up-input" value={validTo || ''} onChange={(e) => setValidTo(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="up-section">
              <div className="up-section-title"><TagIcon size={16} /> Taggar</div>
              <p className="up-input-desc mb-2">Klicka för att markera/avmarkera.</p>
              <div className="d-flex flex-wrap gap-1">
                {tags.length === 0 ? (
                  <span className="text-muted" style={{ fontSize: 13 }}>Inga taggar definierade.</span>
                ) : tags.map((t) => (
                  <button
                    key={t.tagId}
                    type="button"
                    className={`docs-tag ${selectedTags.includes(t.tagId) ? 'active' : ''}`}
                    onClick={() => toggleTag(t.tagId)}
                    style={selectedTags.includes(t.tagId)
                      ? { background: t.color, color: 'white', borderColor: t.color }
                      : { borderColor: t.color, color: t.color }}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="up-modal-actions">
            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Ändringarna sparas på dokumentet (tags + giltighetsdatum).
            </div>
            <div className="d-flex gap-2">
              <button className="up-btn up-btn-cancel" onClick={onClose} disabled={saving}>Avbryt</button>
              <button className="up-btn up-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? <><div className="up-spinner" /> Sparar…</> : <><Save size={16} /> Spara</>}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── CSS ───────────────────────────────────────────────────────────────

const css = `
  .docs-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
  .docs-header-icon { width: 48px; height: 48px; border-radius: 12px; background: var(--rgba-primary-1); color: var(--primary); display: flex; align-items: center; justify-content: center; }
  .docs-title { margin: 0; font-weight: 700; font-size: 1.35rem; color: #111827; }
  .docs-subtitle { margin: 2px 0 0; color: #6b7280; font-size: 0.8125rem; }
  .docs-compliance { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px; background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; font-size: 0.75rem; font-weight: 500; }

  .docs-quota { display: flex; gap: 12px; padding: 12px 14px; background: white; border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 14px; align-items: flex-start; }
  .docs-quota-icon { width: 34px; height: 34px; border-radius: 8px; background: var(--rgba-primary-1); color: var(--primary); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .docs-quota-body { flex: 1; min-width: 0; }
  .docs-quota-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 6px; flex-wrap: wrap; }
  .docs-quota-label { font-size: 0.8125rem; color: #111827; }
  .docs-quota-label strong { font-weight: 600; }
  .docs-quota-pct { margin-left: 6px; color: #6b7280; font-variant-numeric: tabular-nums; }
  .docs-quota-plan { font-size: 0.6875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--primary); padding: 2px 8px; border-radius: 999px; background: var(--rgba-primary-1); }
  .docs-quota-track { height: 8px; background: #f3f4f6; border-radius: 999px; overflow: hidden; }
  .docs-quota-fill { height: 100%; border-radius: 999px; transition: width 0.3s ease, background 0.3s ease; }
  .docs-quota-warning { margin-top: 8px; font-size: 0.75rem; color: #b45309; background: #fef3c7; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 10px; }

  .docs-category-chips { margin-bottom: 10px; }

  .docs-tags-filter { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-bottom: 14px; }
  .docs-tags-label { font-size: 0.75rem; color: #6b7280; display: inline-flex; align-items: center; gap: 4px; font-weight: 500; margin-right: 4px; }
  /* Class is singular doc-tag-chip to match the JSX. Padding bumped so the
     999px border-radius visibly reads as a capsule. */
  .doc-tag-chip { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 999px; font-size: 0.6875rem; font-weight: 500; line-height: 1.4; margin-right: 3px; white-space: nowrap; }


  .docs-main { display: block; }
  .docs-bulk-bar { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: var(--rgba-primary-1); border: 1px solid var(--primary); border-radius: 10px; margin-bottom: 12px; }
  .docs-bulk-info { display: flex; align-items: center; color: var(--primary); font-size: 0.875rem; }
  .docs-bulk-actions { display: flex; gap: 8px; }
  .docs-load-more { display: flex; align-items: center; justify-content: center; padding: 20px; color: #6b7280; font-size: 0.8125rem; }

  /* Selection checkbox on card thumbnails. Always visible when checked;
     hover-revealed otherwise. Solid white fill with a darker grey border +
     subtle shadow so it reads against both light and dark thumbnails. */
  .doc-card-checkbox { position: absolute; top: 6px; left: 6px; width: 24px; height: 24px; border-radius: 6px; border: 1px solid #6b7280; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; opacity: 0; color: var(--primary); box-shadow: 0 1px 4px rgba(0,0,0,0.35); }
  .doc-card-checkbox:hover { border-color: var(--primary); }
  .doc-card:hover .doc-card-checkbox, .doc-card:focus-within .doc-card-checkbox { opacity: 1; }
  .doc-card-checkbox.checked { opacity: 1; background: var(--primary); border-color: var(--primary); color: white; }
  .doc-card.checked { border-color: var(--primary); box-shadow: 0 0 0 2px var(--rgba-primary-2); }
  @media (hover: none) { .doc-card-checkbox { opacity: 1; } }

  .doc-row-checkbox { width: 24px; height: 24px; border: 1px solid #d1d5db; background: white; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7280; }
  .doc-row-checkbox.checked { background: var(--primary); border-color: var(--primary); color: white; }
  .docs-results { min-width: 0; }

  .docs-grid { display: grid; gap: 14px; }
  /* Three density tiers. Documents are portrait — the tile follows the
     document's own aspect to show the whole header clearly. */
  .docs-grid-small  { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
  .docs-grid-medium { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
  .docs-grid-large  { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
  .doc-card { background: white; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; cursor: pointer; transition: all 0.15s; display: flex; flex-direction: column; }
  .doc-card:hover { border-color: var(--primary); box-shadow: 0 4px 12px rgba(0,0,0,0.06); transform: translateY(-1px); }
  .doc-card:focus { outline: 2px solid var(--primary); outline-offset: 2px; }
  .doc-card.selected { border-color: var(--primary); box-shadow: 0 0 0 2px var(--rgba-primary-2); }
  /* Portrait aspect mirrors A4 (1:√2 ≈ 0.71). Using 3/4 for a softer look. */
  .doc-card-thumb { aspect-ratio: 3/4; background: #f9fafb; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; justify-content: center; color: #9ca3af; overflow: hidden; position: relative; }
  .doc-card-thumb img { width: 100%; height: 100%; object-fit: cover; }
  /* PDF thumbnail uses the native browser renderer via <object>. Pointer
     events are blocked so clicks bubble up to the card, and a top-edge
     shadow hides the PDF toolbar bleed-through on some engines. */
  .doc-card-pdf-wrap { width: 100%; height: 100%; pointer-events: none; overflow: hidden; background: white; }
  /* Top-align the rendered page so the thumbnail shows the header/title of
     the document, not the middle of the body. object-fit: cover fills the
     4:3 tile width, height spills over (clipped by overflow: hidden). */
  .doc-card-pdf-wrap img { width: 100%; height: auto; display: block; }
  .doc-card-thumb-fallback { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #9ca3af; }
  /* Hover-reveal action bar on card thumbnails. On touch we always show it. */
  .doc-card-actions { position: absolute; top: 6px; right: 6px; display: flex; gap: 4px; opacity: 0; transition: opacity 0.15s; }
  .doc-card:hover .doc-card-actions, .doc-card:focus-within .doc-card-actions { opacity: 1; }
  @media (hover: none) { .doc-card-actions { opacity: 1; } }
  .doc-card-action { width: 28px; height: 28px; border-radius: 6px; border: 1px solid #9ca3af; background: rgba(255,255,255,0.95); color: #374151; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.2); transition: all 0.15s; }
  .doc-card-action:hover { background: white; color: #111827; border-color: #4b5563; transform: translateY(-1px); }
  .doc-card-action.doc-card-action-danger:hover { color: #dc2626; background: #fef2f2; border-color: #dc2626; }
  /* AI-brief action button. When a brief already exists, tint the
     icon so the user can see at a glance which cards have been
     processed without hovering each one. */
  .doc-card-action.doc-card-action-has-brief { color: #7c3aed; border-color: #c4b5fd; background: #f5f3ff; }
  .doc-card-action.doc-card-action-has-brief:hover { color: #6d28d9; border-color: #7c3aed; background: #ede9fe; }
  .doc-card-action[disabled] { cursor: wait; opacity: 0.7; }

  /* AI-brief — takes the slot the filename normally occupies when
     present. Body text, two lines max, small Sparkles prefix to
     signal it's AI-generated. Filename remains in the title tooltip
     for users who need it. */
  .doc-card-brief {
    display: flex; align-items: flex-start; gap: 5px;
    font-size: 0.75rem; line-height: 1.35; color: #1f2937;
    margin-bottom: 6px;
    min-height: 2.7em; /* reserve 2 lines so cards don't jump in height when brief is shorter */
  }
  .doc-card-brief-icon { color: #7c3aed; flex-shrink: 0; margin-top: 2px; }
  .doc-card-brief-text {
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    overflow: hidden; text-overflow: ellipsis; word-break: break-word;
  }
  /* AI-suggested tag chip — distinguished from human tags by a purple
     tint matching the brief overlay accent. */
  .doc-tag-chip.doc-tag-chip-ai {
    background: #f5f3ff; color: #6d28d9; border: 1px dashed #c4b5fd;
  }
  .doc-row-actions { display: flex; gap: 4px; justify-content: flex-end; }
  .doc-card-body { padding: 10px 12px; }
  .doc-card-name { font-size: 0.8125rem; font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 6px; }
  .doc-card-meta { display: flex; justify-content: space-between; align-items: center; gap: 4px; font-size: 0.6875rem; color: #6b7280; }
  .doc-card-size { font-variant-numeric: tabular-nums; }
  .doc-card-tags { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 6px; }

  .docs-list { background: white; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
  .doc-row { display: grid; grid-template-columns: 28px 32px 1fr 220px 90px 140px 110px; gap: 12px; padding: 10px 14px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: all 0.15s; align-items: center; font-size: 0.8125rem; }
  .doc-row.checked { background: var(--rgba-primary-1); }
  .doc-row:last-child { border-bottom: none; }
  .doc-row:hover { background: #f9fafb; }
  .doc-row.selected { background: var(--rgba-primary-1); }
  .doc-row-icon { color: #6b7280; }
  .doc-row-name { font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 8px; }
  .doc-row-tags { display: inline-flex; gap: 3px; }
  .doc-row-size, .doc-row-date { color: #6b7280; font-variant-numeric: tabular-nums; }
  @media (max-width: 720px) { .doc-row { grid-template-columns: 32px 1fr auto; } .doc-row-source, .doc-row-size, .doc-row-date { display: none; } }

  /* Preview modal — document-viewer aesthetic: branded header, dark stage,
     overlaid prev/next nav, clean metadata strip, keyboard hint in footer. */
  .docs-preview-modal .modal-dialog { max-width: 92vw; }
  .docs-preview-modal .modal-content { border: none; border-radius: 12px; overflow: hidden; }

  .pv-header {
    background: white; padding: 18px 56px 18px 24px;
    border-bottom: 1px solid #e2e8f0;
    position: relative; display: flex; align-items: center;
  }
  .pv-close {
    position: absolute; top: 14px; right: 16px;
    background: white; border: 1px solid #e2e8f0; color: #64748b;
    width: 32px; height: 32px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.2s ease;
  }
  .pv-close:hover { background: #f1f5f9; color: #111827; }
  .pv-header-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pv-title {
    font-size: 1.05rem; font-weight: 600; color: #0f172a;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .pv-subtitle {
    margin-top: 2px; font-size: 0.75rem; color: #64748b;
    display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  }
  /* Stage — neutral light background so the browser's own PDF viewer
     chrome (which is grey/dark) doesn't look like a cutout inside a
     darker frame. */
  .pv-stage {
    background: #f3f4f6; position: relative;
    display: flex; align-items: center; justify-content: center;
    min-height: 65vh; max-height: 78vh; overflow: auto;
  }
  .pv-stage img { max-width: 100%; max-height: 78vh; object-fit: contain; display: block; }
  .pv-stage iframe { width: 100%; height: 78vh; border: 0; background: white; }
  .pv-loading { color: #6b7280; }
  .pv-fallback { text-align: center; padding: 64px; color: #6b7280; }
  .pv-fallback-icon {
    width: 96px; height: 96px; border-radius: 16px;
    background: white; border: 1px solid #e5e7eb;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 16px; color: #9ca3af;
  }
  .pv-fallback-text { font-size: 0.875rem; color: #6b7280; }

  .pv-meta {
    padding: 16px 24px; background: white; border-top: 1px solid #e5e7eb;
    display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 10px 20px; font-size: 0.8125rem; color: #0f172a;
  }
  .pv-meta-row { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .pv-meta-label {
    font-size: 0.6875rem; font-weight: 600; color: #6b7280;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .pv-meta-value {
    color: #111827; display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  }
  .pv-alert {
    grid-column: 1 / -1;
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 500;
  }
  .pv-alert-warn { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
  .pv-alert-info { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }

  .pv-footer {
    background: #f8fafc; padding: 12px 24px; border-top: 1px solid #e2e8f0;
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; flex-wrap: wrap;
  }
  .pv-footer-hint {
    font-size: 0.75rem; color: #64748b;
    font-variant-numeric: tabular-nums;
  }
  .pv-footer-actions { display: flex; align-items: center; gap: 8px; }

  /* Icon-only secondary actions — group visually distinct from the primary
     "Ladda ner" button. */
  .pv-icon-btn {
    width: 36px; height: 36px; border-radius: 8px;
    background: white; border: 1px solid #d1d5db; color: #374151;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s ease;
  }
  .pv-icon-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #9ca3af; color: #0f172a; }
  .pv-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .pv-icon-btn-danger:hover:not(:disabled) { background: #fef2f2; border-color: #dc2626; color: #dc2626; }
  .doc-card-action[disabled] { opacity: 0.35; cursor: not-allowed; }
  .doc-card-action[disabled]:hover { background: rgba(255,255,255,0.95); color: #374151; transform: none; }
`;

export default DocumentsPage;
